import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllGalleries,
  deleteGallery,
  addGallery,
  updateGallery,
} from "../../../../../services/gallerieServices";

import {
  addPhoto,
  deletePhoto,
} from "../../../../../services/photoServices";

import {
  filterAlbums,
  getAlbumsStats,
  getFallbackSectionId,
  getPhotos,
  isDemoMode,
  sortAlbums,
} from "../utils/albumsPhotosUtils";

export default function useAlbumsPhotos(sectionId) {
  const finalSectionId = useMemo(
    () => getFallbackSectionId(sectionId),
    [sectionId]
  );

  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [drawer, setDrawer] = useState({
    type: null,
    album: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchAlbums = useCallback(async () => {
    if (!finalSectionId) {
      setAlbums([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getAllGalleries();
      const safeData = Array.isArray(data) ? data : [];

      const filtered = safeData.filter(
        (album) => Number(album?.section_id) === Number(finalSectionId)
      );

      setAlbums(sortAlbums(filtered));
    } catch (error) {
      console.error("Erreur lors de la récupération des albums :", error);
      toast.error("Erreur lors de la récupération des albums.");
    } finally {
      setLoading(false);
    }
  }, [finalSectionId]);

  useEffect(() => {
    fetchAlbums();
  }, [fetchAlbums]);

  const stats = useMemo(() => getAlbumsStats(albums), [albums]);

  const rows = useMemo(() => {
    return filterAlbums(albums, searchTerm, statusFilter);
  }, [albums, searchTerm, statusFilter]);

  const handleDemoClick = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return true;
    }

    return false;
  };

  const openCreateDrawer = () => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "create",
      album: null,
    });
  };

  const openEditDrawer = (album) => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "edit",
      album,
    });
  };

  const openPhotosDrawer = (album) => {
    setDrawer({
      type: "photos",
      album,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      album: null,
    });
  };

  const handleCreate = async (payload) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      await addGallery({
        ...payload,
        section_id: Number(finalSectionId),
      });

      toast.success("Album créé avec succès.");
      await fetchAlbums();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création de l'album :", error);
      toast.error("Erreur lors de la création de l'album.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (albumId, payload) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      await updateGallery({
        id: albumId,
        ...payload,
        section_id: Number(finalSectionId),
      });

      toast.success("Album modifié avec succès.");
      await fetchAlbums();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la modification de l'album :", error);
      toast.error("Erreur lors de la modification de l'album.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (albumId) => {
    if (handleDemoClick()) return;

    const confirmed = window.confirm("Supprimer cet album photo ?");

    if (!confirmed) return;

    try {
      await deleteGallery(albumId);

      setAlbums((prev) => prev.filter((album) => album.id !== albumId));

      toast.success("Album supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'album :", error);
      toast.error("Erreur lors de la suppression de l'album.");
    }
  };

  const handleAddPhoto = async (albumId, payload) => {
    if (handleDemoClick()) return null;

    try {
      const addedPhoto = await addPhoto({
        ...payload,
        gallerie_id: albumId,
        is_active: true,
      });

      toast.success("Photo ajoutée avec succès.");
      await fetchAlbums();

      return addedPhoto;
    } catch (error) {
      console.error("Erreur lors de l'ajout de la photo :", error);
      toast.error("Erreur lors de l'ajout de la photo.");
      return null;
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (handleDemoClick()) return false;

    const confirmed = window.confirm("Supprimer cette photo ?");

    if (!confirmed) return false;

    try {
      await deletePhoto(photoId);

      toast.success("Photo supprimée avec succès.");
      await fetchAlbums();

      return true;
    } catch (error) {
      console.error("Erreur lors de la suppression de la photo :", error);
      toast.error("Erreur lors de la suppression de la photo.");
      return false;
    }
  };

  const getFreshSelectedAlbum = () => {
    if (!drawer.album?.id) return drawer.album;

    return (
      albums.find((album) => Number(album.id) === Number(drawer.album.id)) ||
      drawer.album
    );
  };

  return {
    finalSectionId,
    albums,
    rows,
    stats,
    loading,
    saving,
    demoMode,

    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,

    drawer,
    selectedAlbum: getFreshSelectedAlbum(),

    openCreateDrawer,
    openEditDrawer,
    openPhotosDrawer,
    closeDrawer,

    fetchAlbums,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleAddPhoto,
    handleDeletePhoto,

    getPhotos,
  };
}